import { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { BuilderFooter } from "./BuilderFooter";
import { Eye, Loader2, Check, AlertCircle } from "lucide-react";
import { useI18n } from "@/hooks/useI18n";
import { useBuilderCapabilities } from "@/lib/builder/capabilities/BuilderCapabilitiesContext";
import { cn } from "@/components/utils/utils";

interface BuilderLayoutProps {
  children: ReactNode;
  onPreview?: () => void;
  onSave?: () => void;
  onCancel?: () => void;
  onExit?: () => void;
  isAutoSaving?: boolean;
  hasUnsaved?: boolean;
  onRetry?: () => void;
  onViewTripRequest?: () => void;
  onRejectItinerary?: (note?: string) => void;
  onAcceptItinerary?: (note?: string) => void;
  onUpdateItineraryNote?: (note: string) => Promise<void>;
  showHeader?: boolean;
  showFooter?: boolean;
  onPreviousStep?: () => void;
  onNextStep?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  isRecapStep?: boolean;
  canProceed?: boolean;
  servicesTotal?: number;
  isLoading?: boolean;
  onRequestChanges?: () => void;
  totalPrice?: number;
  startDate?: string;
  endDate?: string;
  itineraryStatus?: string;
  refundable?: 'full' | 'partial' | 'non_refundable' | null;
  itineraryId?: string | null;
  travelRequestId?: string | null;
  logoSmallUrl?: string | null;
  cancellationPolicyNotes?: string | null;
}

const BuilderLayout = ({
  children,
  onSave,
  onCancel,
  onExit,
  onViewTripRequest,
  onRejectItinerary,
  onAcceptItinerary,
  onUpdateItineraryNote,
  showHeader = true,
  showFooter = true,
  onPreviousStep,
  onNextStep,
  isFirstStep = false,
  isLastStep = false,
  isRecapStep = false,
  canProceed = true,
  servicesTotal,
  isLoading = false,
  isAutoSaving = false,
  hasUnsaved = false,
  onRetry,
  onRequestChanges,
  totalPrice,
  startDate,
  endDate,
  itineraryStatus,
  refundable,
  itineraryId,
  travelRequestId,
  logoSmallUrl,
  cancellationPolicyNotes
}: BuilderLayoutProps) => {
  const { t } = useI18n();
  // Indicatore di salvataggio solo per chi modifica: dalle capacita' del flusso.
  const isReadOnly = !useBuilderCapabilities().canEdit;

  return (
    <div className="h-screen bg-white flex flex-col">
      {/* Header */}
      {showHeader && (
        <header className="flex-shrink-0 border-b border-gray-200 px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Logo */}
              <div className="flex items-center gap-3">
                <img
                  src={
                    logoSmallUrl ||
                    "/assets/images/dark_small.png"
                  }
                  alt="Logo agenzia"
                  className="object-contain transition-all duration-300 ease-in-out h-8 w-8"
                />
                <h4 className="text-gray-900 hidden sm:block">
                  {t("common.itineraryBuilder")}
                </h4>
              </div>
              {onViewTripRequest && (
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onViewTripRequest}
                    className="flex items-center gap-2"
                  >
                    <Eye className="h-4 w-4" />
                    {t("common.viewTripRequest")}
                  </Button>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              {!isReadOnly && !isLoading && (
                <>
                  {isAutoSaving ? (
                    <span className="flex items-center gap-1.5 text-sm text-gray-400 ml-2">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      {t("common.saving")}
                    </span>
                  ) : hasUnsaved ? (
                    <button
                      onClick={onRetry}
                      className="flex items-center gap-1.5 text-sm text-red-600 hover:text-red-800"
                    >
                      <AlertCircle className="h-3.5 w-3.5" />
                      {t("common.saveError")} – {t("common.retry")}
                    </button>
                  ) : (
                    <span className="flex items-center gap-1.5 text-sm text-gray-400 ml-2">
                      <Check className="h-3.5 w-3.5" />
                      {t("common.saved")}
                    </span>
                  )}
                  {onExit && (
                    <Button
                      size="sm"
                      onClick={onExit}
                      variant="outline"
                      className="flex items-center gap-2"
                    >
                      {t("common.exit")}
                    </Button>
                  )}
                </>
              )}
              {isReadOnly && onExit && (
                <Button
                  size="sm"
                  onClick={onExit}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  {t("common.exit")}
                </Button>
              )}
            </div>
          </div>
        </header>
      )}

      {/* Main Content - Scrollable */}
      <main className="flex-1 overflow-y-auto">{children}</main>

      {/* Footer */}
      {showFooter && (
        <BuilderFooter
          isLoading={isLoading}
          isFirstStep={isFirstStep}
          isLastStep={isLastStep}
          isRecapStep={isRecapStep}
          canProceed={canProceed}
          servicesTotal={servicesTotal}
          onCancel={onCancel}
          onPreviousStep={onPreviousStep}
          onNextStep={onNextStep}
          onCreateQuote={onSave}
          onRejectItinerary={onRejectItinerary}
          onAcceptItinerary={onAcceptItinerary}
          onUpdateItineraryNote={onUpdateItineraryNote}
          onRequestChanges={onRequestChanges}
          totalPrice={totalPrice}
          startDate={startDate}
          endDate={endDate}
          itineraryStatus={itineraryStatus}
          refundable={refundable}
          itineraryId={itineraryId}
          travelRequestId={travelRequestId}
          cancellationPolicyNotes={cancellationPolicyNotes}
        />
      )}
    </div>
  );
};

export default BuilderLayout;
